import { Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Money, MoneyOptional } from '../../../core/model/money';
import { InvoiceRow } from '../../model/invoice-row';
import { InvoiceRowFormDefaultsModel } from '../../model/invoice-row-edit';
import { CodingConfig, CodingDefaultValuesConfig } from '../../model/coding';
import { Article } from '../../../rent/model/article';
import { Phrase } from '../../../core/model/phrase';
import { LanguageService } from '../../../core/service/language.service';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { AutoCompleteSelectionState } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';
import { SelectionItem } from '../../../core/component/select/select.component';
import { InvoiceAmountService } from '../../service/invoice-amount.service';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';

export interface InvoiceRowFormContext {
  invoiceRow: InvoiceRow;
  isNew: boolean;
  isReadonly: boolean;
  defaults: InvoiceRowFormDefaultsModel;
  codingDefaultValues: CodingDefaultValuesConfig;
}

@Component({
    selector: 'app-invoice-row-form',
    templateUrl: './invoice-row-form.component.html',
    styleUrls: ['./invoice-row-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InvoiceRowFormComponent implements OnDestroy, IAutoCompleteService<Article> {
  constructor(
    private fb: FormBuilder,
    private cf: FormControlExFactory,
    private languageService: LanguageService,
    private invoiceAmountService: InvoiceAmountService,
    private readonly dimensionLabelsService: DimensionLabelsService
  ) {}

  private _formSubscription: Subscription;
  private _dataContext: InvoiceRowFormContext;
  @Input({ required: true }) set dataContext(context: InvoiceRowFormContext) {
    this._dataContext = context;
    this.initialize();
  }
  get dataContext() {
    return this._dataContext;
  }

  @Output() rowCreated = new EventEmitter<InvoiceRow>();
  @Output() rowChanged = new EventEmitter<InvoiceRow>();
  @Output() rowDeleted = new EventEmitter<InvoiceRow>();
  @Output() canceled = new EventEmitter<void>();

  initialize(): void {
    this.form = null;
    if (!this.dataContext) {
      return;
    }

    this.initializeByDefaults(this.dataContext.defaults);
  }

  isInformation: boolean = false;
  showArticleSelect: boolean;
  initializeByDefaults(val: InvoiceRowFormDefaultsModel) {
    this.isInformation = this.dataContext.invoiceRow.isInformation;
    this.showArticleSelect = !this.isInformation;
    this.initSelections(val);
    this.createForm();
  }

  companyCultureCode: string;
  currencyCode: string;
  articles: Article[];
  codingConfig: CodingConfig;
  costPlaces: SelectionItem[];
  costBearers: SelectionItem[];
  counterparts: SelectionItem[];
  private initSelections(val: InvoiceRowFormDefaultsModel) {
    this.codingConfig = val.codingConfig;
    this.counterparts = this.codingConfig.counterparts.map(x => new SelectionItem(x, x));
    this.costBearers = this.codingConfig.costBearers.map(x => new SelectionItem(x, x));
    this.costPlaces = this.codingConfig.costPlaces.map(x => new SelectionItem(x, x));
    this.counterparts.splice(0, 0, new SelectionItem('NOT_SELECTED_TEXT', null));
    this.costBearers.splice(0, 0, new SelectionItem('NOT_SELECTED_TEXT', null));
    this.costPlaces.splice(0, 0, new SelectionItem('NOT_SELECTED_TEXT', null));
    this.articles = val.articleSelection.map(a => new Article(a));
    this.currencyCode = val.currencyCode;
    this.companyCultureCode = val.companyCultureCode;
  }

  form: FormGroup;
  codingForm: FormGroup;
  createForm(): void {
    this.unsubscribe();
    this._formSubscription = new Subscription();
    const dimensionLabels = this.dimensionLabelsService.fromCodingConfig(this.codingConfig);

    // Default values for coding are drilled down from parent components.
    this.codingForm = this.fb.group({
      costBearer: this.cf.createWithOptions(
        dimensionLabels.costBearerLabel,
        this.dataContext.isNew && this.dataContext.codingDefaultValues?.costBearer
          ? this.dataContext.codingDefaultValues?.costBearer
          : this.dataContext.invoiceRow.coding?.costBearer,
        { isReadonly: this.dataContext.isReadonly }
      ),
      costPlace: this.cf.createWithOptions(
        dimensionLabels.costPlaceLabel,
        this.dataContext.isNew && this.dataContext.codingDefaultValues?.costPlace
          ? this.dataContext.codingDefaultValues?.costPlace
          : this.dataContext.invoiceRow.coding?.costPlace,
        { isReadonly: this.dataContext.isReadonly }
      ),
      counterpart: this.cf.createWithOptions(
        dimensionLabels.counterpartLabel,
        this.dataContext.isNew && this.dataContext.codingDefaultValues?.counterpart
          ? this.dataContext.codingDefaultValues?.counterpart
          : this.dataContext.invoiceRow.coding?.counterpart,
        { isReadonly: this.dataContext.isReadonly }
      ),
      project: this.cf.createWithOptions(
        dimensionLabels.projectLabel,
        this.dataContext.isNew && this.dataContext.codingDefaultValues?.project
          ? this.dataContext.codingDefaultValues?.project
          : this.dataContext.invoiceRow.coding?.project,
        { isReadonly: this.dataContext.isReadonly }
      ),
      function: this.cf.createWithOptions(
        dimensionLabels.functionLabel,
        this.dataContext.isNew && this.dataContext.codingDefaultValues?.function
          ? this.dataContext.codingDefaultValues?.function
          : this.dataContext.invoiceRow.coding?.function,
        { isReadonly: this.dataContext.isReadonly }
      ),
    });

    let needsRequiredValidation = !(this.isInformation || this.dataContext.isReadonly);
    this.form = this.fb.group({
      id: this.cf.createHidden(this.dataContext.invoiceRow.id),
      invoiceId: this.cf.createHidden(this.dataContext.invoiceRow.invoiceId),
      amount: this.cf.createWithOptions(
        'INVOICE_ROW.AMOUNT',
        this.isInformation
          ? new MoneyOptional()
          : new Money(
              this.dataContext.invoiceRow.amount?.currencyCode ?? this.currencyCode,
              this.dataContext.invoiceRow.amount?.amount ?? 0
            ),
        {
          required: needsRequiredValidation,
          isReadonly: this.dataContext.isReadonly,
        }
      ),
      amountIncludingVat: this.cf.createReadonly(
        'INVOICE_ROW.AMOUNT_INC_VAT',
        this.isInformation
          ? new MoneyOptional()
          : new Money(
              this.dataContext.invoiceRow.amountIncludingVat?.currencyCode ?? this.currencyCode,
              this.dataContext.invoiceRow.amountIncludingVat?.amount ?? 0
            )
      ),
      vatAmount: this.cf.createHidden(
        this.isInformation
          ? new MoneyOptional()
          : new Money(
              this.dataContext.invoiceRow.vatAmount?.currencyCode ?? this.currencyCode,
              this.dataContext.invoiceRow.vatAmount?.amount ?? 0
            )
      ),
      articleId: this.cf.createWithOptions('LEASE_AGREEMENT_ROW.ARTICLE', this.dataContext.invoiceRow.articleId, {
        required: needsRequiredValidation,
        isReadonly: this.dataContext.isReadonly,
      }),
      articleCode: this.cf.createHidden(this.dataContext.invoiceRow.articleCode ?? ''),
      description: this.cf.createWithOptions('INVOICE_ROW.DESCRIPTION', this.dataContext.invoiceRow.description, {
        required: needsRequiredValidation,
        isReadonly: this.dataContext.isReadonly,
        length: 200,
      }),
      coding: this.codingForm,
    });
    this._formSubscription.add(this.form.controls['articleId'].valueChanges.subscribe(val => this.changeArticle()));
    this._formSubscription.add(this.form.controls['amount'].valueChanges.subscribe(val => this.updateVat()));

    if (this.dataContext.isNew) {
      this.changeArticle();
    } else {
      this.updateVat();
    }
  }

  get displayCoding(): boolean {
    if (this.isInformation) return false;
    return (
      this.codingConfig.useCostBearer ||
      this.codingConfig.useCostPlace ||
      this.codingConfig.useCounterpart ||
      this.codingConfig.useProject ||
      this.codingConfig.useFunction
    );
  }

  get header(): string {
    if (this.dataContext.invoiceRow.id) {
      if (this.isInformation) {
        return 'INVOICE_ROW.EDIT_INFO_ROW';
      }
      return this.dataContext.isReadonly ? 'INVOICE_ROW.INVOICE_ROW' : 'INVOICE_ROW.EDIT_ROW';
    }
    return this.isInformation ? 'INVOICE_ROW.NEW_INFOROW' : 'INVOICE_ROW.NEW_ROW';
  }

  private getArticle(): Article {
    let id = this.form.controls['articleId'].value;
    if (!id) return null;
    return this.articles.find(a => a.id === id) ?? null;
  }

  changeArticle(): void {
    let article = this.getArticle();
    if (!article) return;
    this.form.controls['articleCode'].setValue(article.code);

    let newInvoiceText = new Phrase(article.invoiceTexts, this.languageService).getTranslationWithFallback(this.companyCultureCode);
    this.form.controls['description'].setValue(newInvoiceText);
    if (article.price?.amount) this.form.controls['amount'].setValue(new Money(article.price.currencyCode, article.price.amount));
    this.updateVat();
  }

  private updateVat() {
    let article = this.getArticle();
    if (!article) return;

    let amount = this.form.controls['amount'].value as Money;
    let amountwithVat = this.invoiceAmountService.calculateAmountInclVat(amount, article.vatPercent);
    this.form.controls['amountIncludingVat'].setValue(amountwithVat);
    this.form.controls['vatAmount'].setValue(new Money(amount.currencyCode, amountwithVat.amount - amount.amount));
  }

  submit() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    Object.assign(this.dataContext.invoiceRow, this.form.value);
    if (this.dataContext.isNew) {
      this.rowCreated.emit(this.dataContext.invoiceRow);
    } else if (this.dataContext.isReadonly) {
      this.canceled.emit();
    } else {
      this.rowChanged.emit(this.dataContext.invoiceRow);
    }

    this.cleanupForm();
  }

  cancel() {
    this.canceled.emit();
    this.cleanupForm();
  }

  get allowDelete(): boolean {
    return !this.dataContext.isReadonly && !this.dataContext.isNew;
  }

  delete() {
    this.rowDeleted.emit(this.dataContext.invoiceRow);
    this.cleanupForm();
  }

  getSelectionItem(id: string, state: AutoCompleteSelectionState<Article, any>): Observable<AutoCompleteSelectionItem<Article>> {
    let article = this.articles.find(a => a.id === id);
    return of(article ? new AutoCompleteSelectionItem(article.toString(), id, article, article.toString()) : null);
  }

  getSelection(filter: string, options: any, page?: number): Observable<AutoCompleteSelectionItem<Article>[]> {
    let articles = this.articles.filter(
      a =>
        filter == '*' ||
        !filter ||
        a.code.toLowerCase().indexOf(filter.toLowerCase()) > -1 ||
        a.caption.toLowerCase().indexOf(filter.toLowerCase()) > -1
    );
    return of(articles.map(article => new AutoCompleteSelectionItem(article.toString(), article.id, article, article.toString())));
  }

  get articleService(): IAutoCompleteService<Article> {
    return this;
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  cleanupForm() {
    this.form = null;
    this.unsubscribe();
  }

  private unsubscribe() {
    this._formSubscription?.unsubscribe();
    this._formSubscription = null;
  }
}
